import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Collapse,
  ListItem,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useTheme } from '@emotion/react';
import { NavLink as RouterLink } from 'react-router-dom';
import { icons } from 'lucide-react';

const useStyles = makeStyles()((theme, _params, classes) => ({
  item: {
    display: 'block',
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: 0,
    paddingRight: "8px"
  },
  itemLeaf: {
    display: 'flex',
    padding: "0 8px",
    margin: "10px 8px",
    width: "220px",
    color: "#A3ACA9",
    position: "relative",
    '&:hover': {
      color: '#A3ACA9',
      backgroundColor: '#232525',
      height: "36px",
      width: "212px",
      borderRadius: "4px",
      paddingRight: 0
    },
    [`&:hover .${classes.icon}`]: {
      color: '#A3ACA9'
    },
    [`&:focus .${classes.icon}`]: {
      color: '#35B797',
    },
    [`&.active .${classes.icon}`]: {
      color: '#35B797',
    },
  },
  button: {
    fontFamily: "Plus Jakarta Sans",
    color: '#A3ACA9',
    padding: '8px 8px 8px 0',
    marginLeft: "8px",
    justifyContent: 'flex-start',
    textTransform: 'none',
    letterSpacing: 0,
    height: "36px",
    width: "212px",
    '&:hover': {
      color: '#E9EBEA',
      backgroundColor: '#232525',
      height: "36px",
      width: "212px"
    },
    [`&:hover .${classes.icon}`]: {
      color: '#A3ACA9'
    },
  },

  metalButtonLeaf: {
    fontFamily: "Plus Jakarta Sans",
    color: '#A3ACA9',
    padding: '8px 8px 8px 0',
    margin: "0 8px",
    justifyContent: 'flex-start',
    textTransform: 'none',
    height: "36px",
    letterSpacing: 0,
    width: '220px',
    fontWeight: theme.typography.fontWeightRegular,
    '&:hover': {
      color: '#E9EBEA',
      backgroundColor: '#232525',
      height: "36px",
      width: "212px"
    },
    [`&:hover .${classes.icon}`]: {
      color: '#A3ACA9'
    },
    [`&:focus .${classes.icon}`]: {
      color: '#35B797',
    },
    [`&.active .${classes.icon}`]: {
      color: '#35B797',
    },
    [`&:hover .${classes.openInNewWindow}`]: {
      display: 'flex'
    },
    [`&.active .${classes.title}`]:{
      color: '#E9EBEA',
    },
    '&.depth-0': {
      [`& .${classes.title}`]: {
        fontWeight: "600",
        fontSynthesis: "none",
      }
    }
  },
  metalButtonLeafActive: {
    fontFamily: "Plus Jakarta Sans",
    color: '#E9EBEA',
    fontWeight: "600",
    fontSynthesis: "none",
    padding: '8px 8px 8px 0',
    justifyContent: 'flex-start',
    textTransform: 'none',
    height: "36px",
    letterSpacing: 0,
    width: '100%',
    '&:hover': {
      color: '#E9EBEA',
      backgroundColor: 'transparent',
      height: "36px",
      width: "212px",
      paddingRight: "0"
    },
    [`&:hover .${classes.title}`]: {
      color: '#E9EBEA',
      backgroundColor: '#232525',
      height: "36px",
      width: "180px",
      borderTopLeftRadius: "4px",
      borderBottomLeftRadius: "4px",
      paddingLeft: "8px",
      display: "flex",
      justifyContent: "flex-start",
      alignItems: "center"
    },
    [`&:hover .${classes.icon}`]: {
      color: '#A3ACA9'
    },
    [`&:focus .${classes.icon}`]: {
      color: '#35B797',
    },
    [`&.active .${classes.icon}`]: {
      color: '#35B797',
    },
    
    [`& .${classes.icon}`]: {
      color: '#35B797',
    },
    '&.depth-0': {
      [`& .${classes.title}`]: {
        fontWeight: "600",
        fontSynthesis: "none",
      }
    }
  },
  icon: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: theme.spacing(1.5),
    marginRight: theme.spacing(1.5),
    minWidth: 20,
    color: '#A3ACA9'
  },
  title: {
    marginRight: 'auto'
  },
}));

const NavItem = ({
  children,
  className,
  depth,
  href,
  icon: Icon,
  info: Info,
  open: openProp,
  testId,
  title,
  ...rest
}) => {
  const { classes, cx } = useStyles();
  const [open, setOpen] = useState(openProp);
  const theme = useTheme();
  const LucideIcon = icons[Icon];
  const ChevronUp = icons['ChevronUp'];
  const ChevronDown = icons['ChevronDown'];

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  let paddingLeft = 8;

  if (depth > 0) {
    paddingLeft = 32 + 8 * depth;
  }

  const style = { paddingLeft };

  if (children) {
    return (
      <ListItem
            className={cx(classes.itemLeaf, className)}
            disableGutters
            key={title}
            {...rest}
          >
        <Button
          className={classes.button}
          onClick={handleToggle}
          style={style}
        >
          <LucideIcon className={classes.icon} size={20} />
          <span className={classes.title}>
            {title}
          </span>
          {open ? <ChevronUp /> : <ChevronDown />}
        </Button>
        <Collapse in={open}>
          {children}
        </Collapse>
      </ListItem>
    );
  }


  return (
    <ListItem
      className={cx(classes.itemLeaf, className)}
      disableGutters
      key={title}
      {...rest}
    >
      <Button
        className={cx(classes.metalButtonLeaf, `depth-${depth}`)}
        component={RouterLink}
        data-testid={testId ? `fdr_viewer_nav_${testId}` : undefined}
        end
        style={style}
        to={href}
      >
        <LucideIcon className={classes.icon} style={{ marginLeft: -1 }} size={20}/>
        <span className={classes.title}>{title}</span>
        {Info && <Info />}
      </Button>
    </ListItem>
  );
};

NavItem.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  depth: PropTypes.number.isRequired,
  href: PropTypes.string,
  icon: PropTypes.object,
  info: PropTypes.elementType,
  open: PropTypes.bool,
  title: PropTypes.string.isRequired,
};

NavItem.defaultProps = {
  open: false
};

export default NavItem;
